<template>
  <!-- ==============================================================
       TopPartsWidget — centralized "Top 5 Most Purchased Parts" panel.

       Two visual modes — INLINE and PILL — mirroring CartWidget.vue's
       collapse/expand architecture exactly so the two widgets read as
       a coherent pair when both are visible on the same host page:

         INLINE: a card-shaped panel ("dashboard-card") with a header
         row carrying the title, period label, and a chevron-right
         collapse button. Renders the user's top 5 parts ranked by
         total quantity ordered (server-supplied), each with a
         basket icon affordance to add it to cart. Hosts an empty
         state for accounts with no order history yet.

         PILL: a side-edge pull tab anchored mid-right to the
         viewport. Styled to match CartWidget.cart-pill — white
         card, red left-accent border, half-rounded radius to tuck
         into the right edge. Carries a bar-chart icon and a vertical
         "TOP 5" caption so its purpose is legible without hover.
         Anchored offset vertically from the cart pill so the two
         tabs stack legibly when both are collapsed.

       The pill is rendered inline (not portal'd) because no
       portal-target named "top-parts-pill" exists in App.vue — and
       z-index: 9000 plus the lack of stacking-context-establishing
       ancestors on #app means the pill anchors to the viewport
       edge correctly without escaping a parent context. CartWidget
       uses a portal because it predates the side-rail rework and
       was originally mounted inside complex containers with their
       own stacking contexts; this widget can side-step that.

       Two operating modes for data:
         CONTROLLED: the host passes `:top-parts="..."` and the
         widget renders that array. State changes (period swap,
         row click) are emitted as events; the host responds.

         STANDALONE: `standalone` prop is true. The widget fetches
         its own data via the insights endpoint, manages period
         locally, and emits row clicks for the host's add-to-cart
         flow. Same pattern CartWidget uses on SavedCarts and
         OrderHistory.

       Either mode can be used on any host. Account uses CONTROLLED
       because it already has `insights.topParts` loaded for the
       insights stat cards. Other host pages that don't have that
       data prefetched can mount the widget with `standalone`.
       ============================================================== -->
  <div class="top-parts-widget"
       :class="{ 'top-parts-floating': floating }"
       v-if="visible && hasOrderHistoryOrLoading">

    <!-- ===== INLINE PANEL =====
         Renders when not collapsed AND the user has visible content
         (or we're still loading and don't yet know whether they do).
         Same .dashboard-card chrome as the original Account markup
         so the visual is byte-identical to what was there before
         the extraction. -->
    <!-- Docked part diagram (Top-5 thumbnail click) — teleports into the
         floating stack's own slot (App.vue) and reuses the global locked-
         image styling. best-effort OCR-zoom to the part's () callout. -->
    <portal to="top5-locked-image-panel" :disabled="!top5Locked">
      <div v-if="top5Locked"
           class="machine-image-wrap is-zoom-locked tpw-docked-wrap">
        <div class="locked-toolbar">
          <button type="button" class="locked-close-btn"
                  title="Close diagram"
                  @click="closeTop5Dock">×</button>
        </div>
        <img :src="top5DockedImg"
             class="machine-preview-img"
             :style="{ transformOrigin: top5Origin, transform: 'scale(' + top5Zoom + ')' }"
             draggable="false"
             alt="Part assembly diagram" />
        <div class="locked-zoom-controls">
          <button type="button" class="locked-zoom-btn" title="Zoom out"
                  @click="top5ZoomOut">&minus;</button>
          <button type="button" class="locked-zoom-btn" title="Zoom in"
                  @click="top5ZoomIn">+</button>
        </div>
      </div>
    </portal>

    <div class="dashboard-card dashboard-card-right top-parts-card"
         v-if="!collapsed">

      <div class="dashboard-card-head">
        <h3 class="dashboard-card-title">
          Top {{ count }} Most Purchased Parts ({{ effectivePeriodLabel }})
        </h3>

        <!-- Collapse-to-pill button. Chevron pointing RIGHT toward
             the rail edge where the pill will appear — same glyph
             CartWidget uses on its collapse button so the affordance
             reads identically across both widgets. -->
        <button type="button"
                class="top-parts-collapse-btn"
                @click="collapse"
                aria-label="Collapse Top parts to side panel"
                title="Collapse Top parts to side">
          <svg xmlns="http://www.w3.org/2000/svg"
               viewBox="0 0 24 24"
               width="14" height="14"
               fill="none"
               stroke="currentColor"
               stroke-width="2.5"
               stroke-linecap="round"
               stroke-linejoin="round"
               aria-hidden="true">
            <polyline points="9 6 15 12 9 18"/>
          </svg>
        </button>
      </div>

      <!-- ===== CONTROLS: period + how-many =====
           Standalone (global) mode only — the widget owns its own data
           and persists these choices to localStorage, so the user's
           YTD/All-Time and Show-N selections are remembered as they
           browse the site (and across reloads). Period switches refetch
           via getInsights(allTime); the count just re-slices the
           already-fetched list (the backend returns up to 50), so
           changing it is instant with no extra request. -->
      <div class="top-parts-controls" v-if="standalone">
        <div class="tp-period" role="group" aria-label="Time period">
          <button type="button"
                  class="tp-toggle-btn"
                  :class="{ 'is-active': period === 'ytd' }"
                  @click="setPeriod('ytd')">YTD</button>
          <button type="button"
                  class="tp-toggle-btn"
                  :class="{ 'is-active': period === 'alltime' }"
                  @click="setPeriod('alltime')">All Time</button>
        </div>
        <label class="tp-show">
          <span class="tp-show-label">Show</span>
          <select class="tp-show-select"
                  :value="count"
                  @change="setCount($event.target.value)"
                  aria-label="How many parts to show">
            <option :value="5">5</option>
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
          </select>
        </label>
      </div>

      <!-- Filled state. The v-if guard moved INSIDE the panel so
           card chrome (red top, shadow, title) always renders —
           only the row content is conditional. New accounts and
           period swaps to a slow window still see the panel
           acknowledging the section exists. -->
      <!-- Part filter — the SAME control as the Search results list (matches part
           code, item #, or price). With a query it searches the WHOLE fetched list
           (up to 50) and shows every match, ignoring the Show-N cap; empty query
           falls back to the top-N slice. -->
      <div class="part-filter-wrap part-filter-wrap-block tpw-filter"
           v-if="effectiveTopParts.length > 0">
        <span class="part-filter-icon" aria-hidden="true">🔍</span>
        <input type="text"
               class="part-filter-input"
               v-model="partFilter"
               placeholder="Filter by part code, item #, or price…"
               aria-label="Filter parts by code, item, or price" />
        <button v-if="partFilter"
                type="button"
                class="part-filter-clear"
                @click="partFilter = ''"
                title="Clear filter">×</button>
      </div>

      <ul class="dashboard-list"
          v-if="displayParts.length > 0">
        <li class="dashboard-list-item"
            v-for="part in displayParts"
            :key="'tpw-' + (part.partId || part.code || part.partCode)">
          <div class="dashboard-list-thumb is-clickable"
               @click="viewOnSearch(part)"
               title="Open this part on the Search page to view & drag its assembly diagram">
            <img v-if="assemblyThumbs[part.assemblyId]"
                 :src="assemblyThumbs[part.assemblyId]"
                 :alt="(part.code || part.partCode || 'Part') + ' image'"
                 loading="lazy" />
            <svg v-else
                 class="dashboard-list-thumb-ph"
                 viewBox="0 0 24 24"
                 aria-hidden="true">
              <path d="M12 2 3 7v10l9 5 9-5V7l-9-5zm0 2.31 6.5 3.61L12 11.5 5.5 7.92 12 4.31zM5 9.42l6 3.33v6.94l-6-3.33V9.42zm14 0v6.94l-6 3.33v-6.94l6-3.33z"/>
            </svg>
          </div>
          <div class="dashboard-list-main">
            <div class="dashboard-list-code">{{ part.code || part.partCode }}</div>
            <div class="dashboard-list-desc">
              {{ part.description }}
            </div>
            <div class="dashboard-list-meta">
              Ordered <strong>{{ part.timesOrdered }}</strong>
              {{ part.timesOrdered === 1 ? 'time' : 'times' }} ·
              <strong>{{ part.totalQuantity }}</strong> qty
            </div>
            <!-- Price + QTY/MACH, shown like the search results (request: full
                 match). price/quantityPerMachine come from the most-purchased
                 endpoint; rendered only once present. -->
            <div class="dashboard-list-price" v-if="part.price">
              {{ formatPrice(part.price) }}<span class="dashboard-list-qpm" v-if="part.quantityPerMachine"> · QTY/MACH: {{ part.quantityPerMachine }}</span>
            </div>
          </div>
          <!-- Item # box (assembly callout) — same as the search results so the
               two lists match. Already present in the most-purchased data. -->
          <div class="dashboard-list-item-box is-clickable"
               v-if="part.partItem"
               role="button"
               @click="viewOnSearch(part)"
               title="Open this part on the Search page to view its assembly diagram">
            <span class="dashboard-list-item-label">Item</span>
            <span class="dashboard-list-item-val">{{ part.partItem }}</span>
          </div>

          <!-- Basket / pencil icon. Always emit the same `part-click`
               event upward — the host decides whether to open the
               add modal in 'add' or 'edit' mode based on its own
               staged-cart state. The widget itself doesn't know
               what's in the cart, which keeps it host-agnostic.

               When the host passes `:staged-part-ids`, the widget
               swaps the cart icon for a pencil to surface the
               "already in cart, click to edit" affordance. That's
               purely cosmetic; the same event fires either way. -->
          <a href="#"
             class="dashboard-add-btn"
             :class="{ 'is-edit': isStaged(part) }"
             :title="(isStaged(part) ? 'Edit ' : 'Add ') + (part.code || part.partCode) + (isStaged(part) ? ' in your cart' : ' to cart')"
             @click.prevent="onPartClick(part)">
            <svg v-if="!isStaged(part)"
                 class="cart-icon-svg"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 576 512"
                 width="14" height="14"
                 aria-hidden="true">
              <path fill="currentColor"
                d="M0 24C0 10.7 10.7 0 24 0H69.5c22 0 41.5 12.8 50.6 32h411c26.3 0 45.5 25 38.6 50.4l-41 152.3c-8.5 31.4-37 53.3-69.5 53.3H170.7l5.4 28.5c2.2 11.3 12.1 19.5 23.6 19.5H488c13.3 0 24 10.7 24 24s-10.7 24-24 24H199.7c-34.6 0-64.3-24.6-70.7-58.5L77.4 54.5c-.7-3.8-4-6.5-7.9-6.5H24C10.7 48 0 37.3 0 24zM128 464a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zm336-48a48 48 0 1 1 0 96 48 48 0 1 1 0-96z"/>
            </svg>
            <svg v-else
                 class="cart-icon-svg edit-icon-svg"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 512 512"
                 width="13" height="13"
                 aria-hidden="true">
              <path fill="currentColor"
                d="M362.7 19.3L314.3 67.7 444.3 197.7l48.4-48.4c25-25 25-65.5 0-90.5L453.3 19.3c-25-25-65.5-25-90.5 0zm-71 71L58.6 323.5c-10.4 10.4-18 23.3-22.2 37.4L1 481.2C-1.5 489.7 .8 498.8 7 505s15.3 8.5 23.7 6.1l120.3-35.4c14.1-4.2 27-11.8 37.4-22.2L421.7 220.3 291.7 90.3z"/>
            </svg>
          </a>
        </li>
      </ul>

      <!-- Filter is active but nothing matched — offer a one-click clear (mirrors
           the Search results' "clear filter" affordance). -->
      <div v-else-if="effectiveTopParts.length > 0" class="dashboard-empty tpw-no-match">
        No parts match “{{ partFilter }}”.
        <a href="#" class="tpw-filter-clear-link" @click.prevent="partFilter = ''">Clear filter</a>
      </div>

      <!-- Empty state. Same copy as the original Account markup so
           existing accounts that recognized the wording aren't
           surprised. The period label in the title above already
           tells the user which window we're checking, so this stays
           generic. -->
      <div v-else class="dashboard-empty">
        No purchase history yet.
      </div>
    </div>

    <!-- ===== COLLAPSED PILL =====
         Side-edge pull tab. Rendered inline (not portal'd) — see
         the top-of-file comment for rationale. Click anywhere to
         expand. Positioning offset from CartWidget.cart-pill's
         mid-right anchor so the two tabs don't overlap when both
         are simultaneously collapsed. -->
    <portal to="top-parts-pill">
    <div class="top-parts-pill"
         v-if="collapsed"
         @click="expand"
         role="button"
         tabindex="0"
         @keydown.enter.space.prevent="expand"
         aria-label="Open Top 5 most purchased parts"
         title="Open Top 5 most purchased parts">
      <div class="top-parts-pill-icon">
        <!-- Line-chart icon — a trend line over L-shaped axes. Reads
             as "top / most-purchased ranking" and is distinct both from
             the cart glyph on CartWidget and from the previous bar
             icon, which read like wireless/signal bars. -->
        <svg xmlns="http://www.w3.org/2000/svg"
             viewBox="0 0 24 24"
             width="22" height="22"
             fill="none"
             stroke="currentColor"
             stroke-width="2"
             stroke-linecap="round"
             stroke-linejoin="round"
             aria-hidden="true">
          <path d="M4 4v16h16"/>
          <polyline points="7.5 14.5 11 10.5 14 13 19.5 6.5"/>
        </svg>
      </div>
      <!-- Vertical caption so the pill's purpose is legible without
           hover. Letter-spacing matches dashboard-card-title for
           visual continuity with the expanded panel. -->
      <div class="top-parts-pill-label">TOP {{ count }}</div>
    </div>
    </portal>

    <!-- ===== ADD-TO-CART MODAL (standalone/global only) =====
         When the widget owns its data (standalone mode, e.g. the
         global mount in App.vue), it also owns the add-to-cart
         interaction: clicking a row's basket icon opens this modal
         to pick a destination (order/quote), a PO/quote reference,
         and a quantity, then writes straight to the server cart and
         broadcasts `cart-updated` so the global CartWidget + header
         count refresh. Controlled hosts (which pass :top-parts) keep
         receiving the `part-click` event and run their own flow, so
         this modal stays dormant there (showAddModal never opens).

         Class names + modal-class match the global .add-to-cart-*
         rules so the look is identical to the original dashboard
         modal these were ported from. -->
    <b-modal id="topPartsAddModal"
             v-model="showAddModal"
             title="Add One of Your Top Purchased Parts"
             :ok-title="addingPart ? 'Adding…' : 'Add to Cart'"
             ok-variant="primary"
             cancel-title="Cancel"
             cancel-variant="outline-dark"
             :ok-disabled="!canConfirm || addingPart"
             size="md"
             centered
             hide-header-close
             no-close-on-backdrop
             no-close-on-esc
             modal-class="add-to-cart-modal"
             @ok="onAddConfirm"
             @cancel="onAddCancel"
             @shown="onAddShown">
      <div class="add-to-cart-body">
        <p class="add-to-cart-lede" v-if="pendingPart">
          Adding
          <strong class="add-to-cart-partcode">{{ pendingPart.partCode }}</strong>
          <span v-if="pendingPart.description">({{ pendingPart.description }})</span>
          to your cart.
        </p>

        <label class="add-to-cart-section-label">Destination</label>
        <div class="add-to-cart-destination">
          <button type="button"
                  class="add-to-cart-dest-btn"
                  :class="{ 'is-active': pendingDestination === 'order' }"
                  @click="onPickDestination('order')">
            <span class="add-to-cart-dest-title">Order</span>
            <span class="add-to-cart-dest-sub">Add to your active order</span>
          </button>
          <button type="button"
                  class="add-to-cart-dest-btn"
                  :class="{ 'is-active': pendingDestination === 'quote' }"
                  @click="onPickDestination('quote')">
            <span class="add-to-cart-dest-title">Quote</span>
            <span class="add-to-cart-dest-sub">Add to a price quote</span>
          </button>
        </div>

        <template v-if="pendingDestination">
          <template v-if="currentDestinationGroups.length > 0 && !useNewPo">
            <label class="add-to-cart-section-label">
              {{ pendingDestination === 'quote'
                 ? 'Add to existing quote or start new'
                 : 'Add to existing order or start new' }}
            </label>
            <div class="add-to-cart-po-picker">
              <button type="button"
                      class="add-to-cart-po-pick-btn"
                      v-for="g in currentDestinationGroups"
                      :key="'tpw-pick-' + g.poNumber"
                      :class="{ 'is-active': pendingPoNumber === g.poNumber }"
                      @click="onPickExistingPo(g.poNumber)">
                <span class="add-to-cart-po-pick-btn-num">
                  {{ pendingDestination === 'quote' ? 'Quote Reference' : 'PO' }}: {{ g.poNumber }}
                </span>
                <span class="add-to-cart-po-pick-btn-meta">
                  {{ g.count }} {{ g.count === 1 ? 'part' : 'parts' }}
                </span>
              </button>
              <button type="button"
                      class="add-to-cart-po-pick-btn add-to-cart-po-pick-btn-new"
                      @click="onPickNewPo()">
                <span class="add-to-cart-po-pick-btn-num">
                  + New {{ pendingDestination === 'quote' ? 'Quote Reference' : 'PO Number' }}
                </span>
              </button>
            </div>
          </template>

          <template v-if="currentDestinationGroups.length === 0 || useNewPo">
            <label for="topPartsPoInput" class="add-to-cart-section-label">
              {{ pendingDestination === 'quote' ? 'Quote Reference' : 'PO Number' }}
            </label>
            <input id="topPartsPoInput"
                   type="text"
                   v-model="pendingPoNumber"
                   :placeholder="pendingDestination === 'quote'
                     ? 'Enter Quote Reference'
                     : 'Enter PO Number'"
                   class="po-modal-input"
                   autocomplete="off"
                   @keyup.enter="onAddConfirm" />
          </template>

          <label for="topPartsQtyInput" class="add-to-cart-section-label">
            Quantity
          </label>
          <input id="topPartsQtyInput"
                 type="number"
                 min="1"
                 step="1"
                 v-model.number="pendingQuantity"
                 class="po-modal-input add-to-cart-qty-input"
                 @keyup.enter="onAddConfirm" />
        </template>
      </div>
    </b-modal>

  </div>
</template>

<script>
import Vue from "vue";
import FeApiService from "@/assets/js/FeApiService";
import { locateCallout } from "@/assets/js/calloutLocator";

const apiService = new FeApiService();

// localStorage key for the collapsed/expanded preference. Same
// pattern as CartWidget's jcsteele.cart.collapsed key — picking a
// stable, namespaced key means the user's choice survives across
// reloads, route changes, and sessions.
const COLLAPSE_STORAGE_KEY = "jcsteele.topparts.collapsed";
// Period (YTD vs All Time) and how-many-to-show preferences. Same
// namespaced-localStorage pattern as the collapse key so the user's
// choices persist across pages, reloads, and sessions.
const PERIOD_STORAGE_KEY = "jcsteele.topparts.period";
const COUNT_STORAGE_KEY = "jcsteele.topparts.count";
// Allowed "Show N" options for the count selector. The backend returns
// up to 50 top parts, so these all slice from one fetch (no refetch).
const COUNT_OPTIONS = [5, 10, 20, 50];

export default {
  name: "TopPartsWidget",

  props: {
    // ===== CONTROLLED MODE =====
    // The host page passes the array of part objects it wants
    // rendered. Each part should at minimum carry:
    //   - partId  (or code/partCode used as the v-for key)
    //   - code or partCode  (rendered as the row's primary text)
    //   - description       (secondary text)
    //   - timesOrdered      (numeric)
    //   - totalQuantity     (numeric)
    // Anything missing falls back gracefully — code/partCode have
    // a `||` fallback chain, and missing numeric values just render
    // as blank.
    topParts: {
      type: Array,
      default: null,
    },

    // Label rendered in the title parens — typically "YTD" or
    // "All Time" depending on the host page's period state. The
    // widget itself doesn't switch periods (that's a host concern
    // since the same period typically drives other UI on the page
    // — e.g. the insight stat cards on Account). Default is YTD
    // so first-paint reads correctly even without the prop.
    periodLabel: {
      type: String,
      default: "YTD",
    },

    // ===== STANDALONE MODE =====
    // Set true on hosts that don't otherwise load insights data.
    // The widget fetches its own top-parts via the insights
    // endpoint and manages an internal copy. In standalone mode
    // the topParts prop is ignored.
    standalone: {
      type: Boolean,
      default: false,
    },

    // ===== STAGED PART INDICATOR =====
    // Optional array of "things currently in the cart" identifiers
    // the widget uses to flip the row icon from cart → pencil.
    // Accepts an array of strings (partIds), numbers, or objects
    // with a partId field — whichever the host already has.
    // When omitted, the row icon is always the cart glyph.
    stagedPartIds: {
      type: Array,
      default: function () {
        return [];
      },
    },

    // ===== VISIBILITY GATE =====
    // Hosts that conditionally show the panel (e.g. only when a
    // company is selected) can drive it through here instead of
    // wrapping the widget in v-if at the call site. Keeps the
    // host-side template tidy. Defaults to true so the widget
    // is visible wherever it's mounted.
    visible: {
      type: Boolean,
      default: true,
    },

    // Floating mode. Set true when the widget is mounted in the
    // global right-edge stack in App.vue rather than inside a host
    // page's grid column. The collapsed pill already floats on its
    // own; this flag stops the expanded card from stretching to fill
    // a flex column (it sizes to content in the stack) and caps its
    // height so a tall list scrolls internally. Default false keeps
    // the original column-fill behavior for inline hosts.
    floating: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // Standalone mode only. Populated by self-fetching the
      // insights endpoint in mounted(). Empty in controlled mode.
      internalTopParts: [],

      // Standalone mode only. Tracks whether the initial fetch
      // has resolved so the empty state doesn't flash before the
      // server has spoken. In controlled mode this is irrelevant
      // (the host owns the data), so it stays true.
      internalLoaded: false,

      // Set true once mounted() decides this account has any
      // order history at all. Drives the top-level v-if so the
      // widget hides ENTIRELY on brand-new accounts with zero
      // history (rather than rendering an empty card forever).
      hasAnyHistory: false,

      // Collapsed-to-pill state. Hydrated from localStorage in
      // created() before first render so we don't flash one
      // state then snap to the other.
      collapsed: false,

      // ===== STANDALONE PERIOD + COUNT (persisted) =====
      // period: 'ytd' | 'alltime' — which window getInsights queries.
      // count: how many ranked parts to show (5/10/20/50). Both are
      // hydrated from localStorage in created() and re-persisted on
      // change so the user's choices are remembered while browsing and
      // across reloads.
      period: "ytd",
      count: 5,
      // Live filter text for the parts list — same behavior as the Search results
      // filter (matches part code / item # / price, case-insensitive substring).
      partFilter: "",
      // ===== PART THUMBNAILS =====
      // Map of assemblyId -> blob image URL (null = no image on file /
      // failed / in-flight). Each Top-5 row shows its part's assembly
      // picture as a small left-hand icon, matching the Search results
      // list. machineImageUrl is the blob container prefix (the same one
      // Search/Index.vue uses for assembly images).
      assemblyThumbs: {},
      machineImageUrl: "https://steeleimages.blob.core.windows.net/images/",
      // ===== DOCKED PART DIAGRAM (Top-5 thumbnail click) =====
      // Clicking a row's thumbnail docks that part's assembly diagram into
      // the floating stack (its own top5-locked-image-panel slot in
      // App.vue) and best-effort OCR-zooms to the part's () callout —
      // mirroring the Search results behavior.
      top5DockedImg: null,
      top5Locked: false,
      top5Origin: "50% 50%",
      top5Zoom: 4,
      // Sticky: set true the first time a fetch returns any parts and
      // never reset for the life of this (single, global) instance.
      // Keeps the panel — and its period toggle — visible if the user
      // switches to a window that happens to be empty (e.g. All Time has
      // parts but this year doesn't) so they can switch back instead of
      // the whole widget vanishing.
      hasEverHadParts: false,

      // Cart-bus listener handle so we can $off it in
      // beforeDestroy and avoid stale-closure NREs after the
      // component is gone. Standalone mode only.
      _cartBusHandler: null,

      // ===== ADD-TO-CART MODAL STATE (standalone/global only) =====
      // Drives the b-modal above. pendingPart is the normalized
      // top-part being added; pendingDestination starts null so the
      // user must explicitly choose Order or Quote. orderPoGroups /
      // quotePoGroups are the existing POs on the live server cart
      // (fetched when the modal opens) so the picker can offer
      // "add to existing PO" vs "new PO".
      showAddModal: false,
      pendingPart: null,
      pendingDestination: null,
      pendingPoNumber: "",
      pendingQuantity: 1,
      useNewPo: false,
      addingPart: false,
      orderPoGroups: [],
      quotePoGroups: [],
    };
  },

  computed: {
    // Single source the template reads from. In controlled mode
    // it's the prop; in standalone mode it's our internal copy.
    // Centralising here means the template stays mode-agnostic.
    effectiveTopParts() {
      if (this.standalone) {
        return this.internalTopParts || [];
      }
      return this.topParts || [];
    },

    // The rows actually rendered. No filter → the top-N slice (Show-N). With a
    // filter → every match across the whole fetched list (mirrors the Search
    // results filter: matches part code, item #, or price; case-insensitive
    // substring), so a part ranked beyond N is still findable.
    displayParts() {
      var list = this.effectiveTopParts || [];
      var q = (this.partFilter || "").trim().toLowerCase();
      if (!q) return list.slice(0, this.count);
      return list.filter(function (p) {
        return (
          String(p.code || p.partCode || "").toLowerCase().indexOf(q) >= 0 ||
          String(p.partItem || "").toLowerCase().indexOf(q) >= 0 ||
          String(p.price == null ? "" : p.price).toLowerCase().indexOf(q) >= 0
        );
      });
    },

    // Period label visible in the panel header. In standalone mode it
    // reflects the widget's own YTD / All Time toggle; in controlled
    // mode it comes from the host's periodLabel prop.
    effectivePeriodLabel() {
      if (this.standalone) {
        return this.period === "alltime" ? "All Time" : "YTD";
      }
      return this.periodLabel || "YTD";
    },

    // Fast lookup table for the "is this part staged in cart?"
    // check on each row. Built from the stagedPartIds prop;
    // accepts strings/numbers OR objects with a partId field
    // so hosts can pass whatever shape they already have without
    // mapping first. Recomputed only when the prop changes.
    stagedIdLookup() {
      var lookup = Object.create(null);
      var ids = this.stagedPartIds || [];
      for (var i = 0; i < ids.length; i++) {
        var v = ids[i];
        if (v == null) continue;
        if (typeof v === "object") {
          if (v.partId != null) lookup[String(v.partId)] = true;
          if (v.code != null) lookup[String(v.code)] = true;
          if (v.partCode != null) lookup[String(v.partCode)] = true;
        } else {
          lookup[String(v)] = true;
        }
      }
      return lookup;
    },

    // Whether the widget should render at all. Hides on brand-
    // new accounts (no order history) since "Top 5 of nothing"
    // adds no signal. In controlled mode we trust the host: if
    // they passed an array we render it (the empty state inside
    // covers the no-history case). In standalone we wait for the
    // fetch to resolve before deciding.
    hasOrderHistoryOrLoading() {
      if (!this.standalone) {
        // Controlled — the host has decided to mount us, so
        // we render. The empty state takes over if the array
        // is empty.
        return true;
      }
      // Standalone — render while loading (so first paint is
      // visible immediately) and after load if any history
      // was returned. Hide entirely after a successful load
      // that returned zero rows AND the host didn't tell us
      // there's any history elsewhere on the page.
      if (!this.internalLoaded) return true;
      // hasEverHadParts keeps the panel (and its period toggle) visible
      // once any window has shown data, so switching to an empty window
      // doesn't make the whole widget disappear out from under the user.
      return this.hasAnyHistory || this.hasEverHadParts;
    },

    // Drives the inline-visibility-change event the host uses to
    // collapse its right-column when the panel goes to the pill.
    // Mirrors CartWidget.inlineVisible exactly.
    inlineVisible() {
      return (
        this.visible &&
        !this.collapsed &&
        this.hasOrderHistoryOrLoading
      );
    },

    // Existing POs/quote-references on the live server cart for the
    // currently-chosen destination — drives the modal's PO picker.
    currentDestinationGroups() {
      return this.pendingDestination === "quote"
        ? this.quotePoGroups
        : this.orderPoGroups;
    },

    // Gate for the modal's OK button: a destination, a non-blank
    // PO/reference, and a quantity >= 1.
    canConfirm() {
      if (!this.pendingDestination) return false;
      if (!(this.pendingPoNumber || "").trim()) return false;
      var q = Number(this.pendingQuantity);
      return isFinite(q) && q >= 1;
    },
  },

  created() {
    // Hydrate persisted UI prefs BEFORE first render so we don't flash
    // a default then snap to the stored choice. localStorage may throw
    // in private-mode iOS or with cookies disabled; the catch leaves us
    // at the defaults (expanded / YTD / 5).
    try {
      var raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
      this.collapsed = raw === "1" || raw === "true";

      var p = localStorage.getItem(PERIOD_STORAGE_KEY);
      if (p === "alltime" || p === "ytd") {
        this.period = p;
      }

      var c = parseInt(localStorage.getItem(COUNT_STORAGE_KEY), 10);
      if (COUNT_OPTIONS.indexOf(c) !== -1) {
        this.count = c;
      }
    } catch (e) {
      /* no-op */
    }
  },

  mounted() {
    if (this.standalone) {
      this.fetchTopParts();

      // Standalone mode: stay in sync with cart-driven activity
      // elsewhere in the app. When a part is placed (order goes
      // through), the top-parts list could shift — refetch on
      // the cart-updated bus event so a freshly-completed order
      // is reflected. CartWidget uses the same bus.
      this._cartBusHandler = () => {
        this.fetchTopParts();
      };
      if (Vue.prototype.$cartBus) {
        Vue.prototype.$cartBus.$on(
          "cart-updated",
          this._cartBusHandler
        );
      }
    } else {
      // Controlled mode: trust the host to populate the array.
      // We just need to decide whether to ever render — assume
      // the host knows what they're doing and let the empty
      // state speak for itself if the array's empty.
      this.hasAnyHistory =
        (this.topParts || []).length > 0;
    }
    // Single docked image across the app: when another source (the Search
    // results list) claims the dock, close ours so only one shows at a time.
    this._dockClaimHandler = (src) => {
      if (src !== "top5") this.closeTop5Dock();
    };
    if (Vue.prototype.$cartBus) {
      Vue.prototype.$cartBus.$on("dock-claimed", this._dockClaimHandler);
    }
  },

  beforeDestroy() {
    // Detach the cart-bus listener so a stale closure doesn't
    // keep firing into a destroyed component (would NRE on
    // internalTopParts since reactivity is gone). No-op if we
    // never attached.
    if (Vue.prototype.$cartBus && this._cartBusHandler) {
      Vue.prototype.$cartBus.$off(
        "cart-updated",
        this._cartBusHandler
      );
    }
    if (Vue.prototype.$cartBus && this._dockClaimHandler) {
      Vue.prototype.$cartBus.$off("dock-claimed", this._dockClaimHandler);
    }
  },

  watch: {
    // Re-evaluate hasAnyHistory when the host swaps in a new
    // topParts array. Without this, a host that fetches data
    // AFTER mounting the widget would see hasAnyHistory stuck
    // at the initial-mount value (typically false → widget
    // hidden). Immediate so the first render decides correctly
    // even on synchronous-data hosts.
    topParts: {
      handler: function (v) {
        if (!this.standalone) {
          this.hasAnyHistory = (v || []).length > 0;
        }
      },
      immediate: true,
    },
    // Emit every time the inline panel's visibility changes so
    // the host page can react (collapse its hosting column to
    // col-12, hide a header section, whatever). Immediate so
    // the first render also tells the host where things stand
    // — covers the case where the widget hydrates already-
    // collapsed and the host needs to know up-front.
    inlineVisible: {
      handler: function (v) {
        this.$emit("inline-visibility-change", v);
      },
      immediate: true,
    },
    // Filtering can surface a part ranked beyond the Show-N slice whose assembly
    // thumbnail hasn't been fetched yet — top up the cache for the new matches.
    partFilter: function () {
      this.loadTopPartThumbnails();
    },
  },

  methods: {
    // Format a part price like the results' currency filter, but self-contained
    // so the widget carries no external filter dependency.
    formatPrice(v) {
      var n = Number(v) || 0;
      return "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    // ===== COLLAPSE / EXPAND =====
    // Same shape as CartWidget so the two widgets feel like a
    // pair to the user. Persist to localStorage so the choice
    // survives reloads and navigation.
    collapse() {
      this.collapsed = true;
      this._persistCollapsed(true);
    },
    expand() {
      this.collapsed = false;
      this._persistCollapsed(false);
    },
    _persistCollapsed(v) {
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, v ? "1" : "0");
      } catch (e) {
        /* no-op */
      }
    },

    // ===== PERIOD (YTD / ALL TIME) =====
    // Standalone only. Switch the window, persist it, and refetch
    // (getInsights takes allTime). No-op if unchanged.
    setPeriod(p) {
      var clean = p === "alltime" ? "alltime" : "ytd";
      if (clean === this.period) return;
      this.period = clean;
      try {
        localStorage.setItem(PERIOD_STORAGE_KEY, clean);
      } catch (e) {
        /* no-op */
      }
      if (this.standalone) {
        this.fetchTopParts();
      }
    },

    // ===== COUNT (SHOW N) =====
    // How many ranked parts to show. Persisted; no refetch needed —
    // the backend returns up to 50 and the template slices to count.
    setCount(v) {
      var n = parseInt(v, 10);
      if (COUNT_OPTIONS.indexOf(n) === -1) return;
      this.count = n;
      // Showing more rows may reveal parts whose assembly image hasn't
      // been fetched yet — top up the thumbnail cache for the new slice.
      this.loadTopPartThumbnails();
      try {
        localStorage.setItem(COUNT_STORAGE_KEY, String(n));
      } catch (e) {
        /* no-op */
      }
    },

    // ===== STAGED INDICATOR =====
    // Used by the template to decide whether a row's icon is
    // the cart glyph (add) or the pencil glyph (edit). Reads
    // through the stagedIdLookup computed for O(1) per row.
    isStaged(part) {
      if (!part) return false;
      var lookup = this.stagedIdLookup;
      if (part.partId != null && lookup[String(part.partId)]) return true;
      if (part.code != null && lookup[String(part.code)]) return true;
      if (part.partCode != null && lookup[String(part.partCode)]) return true;
      return false;
    },

    // ===== ROW CLICK =====
    // Controlled hosts (which pass :top-parts) listen for this event
    // and run their own add-to-cart flow. The standalone/global
    // instance owns the flow itself and opens the modal below — so
    // the single global widget can add to the cart from any page.
    onPartClick(part) {
      this.$emit("part-click", { part: part });
      if (this.standalone) {
        this.openAddModal(part);
      }
    },

    // ===== ADD-TO-CART MODAL (standalone/global only) =====
    // Normalize the clicked top-part into the modal's shape and open
    // it. Top-5 rows use `code`; the modal uses `partCode`.
    openAddModal(part) {
      if (!part) return;
      var mapped = {
        partId: part.partId,
        partCode: part.code || part.partCode,
        description: part.description,
        totalQuantity: part.totalQuantity,
        timesOrdered: part.timesOrdered,
      };
      if (!mapped.partId || !mapped.partCode) return;
      this.pendingPart = mapped;
      this.pendingDestination = null;
      this.useNewPo = false;
      this.pendingPoNumber = "";
      this.pendingQuantity = this.defaultQuantityFor(mapped);
      this.orderPoGroups = [];
      this.quotePoGroups = [];
      this.fetchActiveCartGroups();
      this.showAddModal = true;
    },

    // Pull existing POs off the live server carts so the picker can
    // offer "add to existing PO" vs "new PO". Fire-and-forget: the
    // modal opens immediately; the picker fills in as fetches resolve.
    fetchActiveCartGroups() {
      var self = this;
      var orderId = parseInt(localStorage.cartId, 10) || 0;
      var quoteId = parseInt(localStorage.cartIdRFQ, 10) || 0;
      if (orderId) {
        apiService
          .getCart({ id: orderId })
          .then(function (cart) {
            self.orderPoGroups = self._derivePoGroups(cart, false);
          })
          .catch(function () {
            self.orderPoGroups = [];
          });
      }
      if (quoteId) {
        apiService
          .getCart({ id: quoteId })
          .then(function (cart) {
            self.quotePoGroups = self._derivePoGroups(cart, true);
          })
          .catch(function () {
            self.quotePoGroups = [];
          });
      }
    },
    _derivePoGroups(cart, wantQuote) {
      if (!cart) return [];
      var isQuote =
        cart.discriminator === "Rfq" ||
        cart.Discriminator === "Rfq" ||
        cart.discriminator === 1 ||
        cart.Discriminator === 1;
      if (wantQuote !== isQuote) return [];
      return (cart.purchaseOrders || [])
        .map(function (po) {
          return { poNumber: po.number || "", count: (po.parts || []).length };
        })
        .filter(function (g) {
          return g.poNumber;
        });
    },

    onPickDestination(dest) {
      this.pendingDestination = dest === "quote" ? "quote" : "order";
      this.pendingPoNumber = "";
      this.useNewPo = this.currentDestinationGroups.length === 0;
    },
    onPickExistingPo(poNumber) {
      this.pendingPoNumber = poNumber;
      this.useNewPo = false;
    },
    onPickNewPo() {
      this.pendingPoNumber = "";
      this.useNewPo = true;
      this.$nextTick(() => {
        var el = document.getElementById("topPartsPoInput");
        if (el) el.focus();
      });
    },
    onAddShown() {
      /* Destination must be chosen first, so nothing to focus yet. */
    },
    onAddCancel() {
      this.showAddModal = false;
      this.resetAddModal();
    },
    resetAddModal() {
      this.pendingPart = null;
      this.pendingDestination = null;
      this.pendingPoNumber = "";
      this.pendingQuantity = 1;
      this.useNewPo = false;
      this.orderPoGroups = [];
      this.quotePoGroups = [];
    },

    // Sensible starting quantity from the user's own history:
    // average qty per order (total / times), min 1.
    defaultQuantityFor(part) {
      if (!part) return 1;
      var total = Number(part.totalQuantity) || 0;
      var times = Number(part.timesOrdered) || 1;
      if (total <= 0 || times <= 0) return 1;
      return Math.max(1, Math.round(total / times));
    },

    // ===== CONFIRM → WRITE TO SERVER CART =====
    // Resolve the part's machine/assembly context (required by the
    // saveCart payload), POST it to the destination cart, then
    // broadcast cart-updated. saveCart with an existing cart id + the
    // single new part appends server-side (same as the dashboard flow
    // this was ported from).
    async onAddConfirm(bvModalEvent) {
      if (bvModalEvent && bvModalEvent.preventDefault) {
        bvModalEvent.preventDefault();
      }
      if (this.addingPart) return;
      if (!this.pendingDestination) return;
      var po = (this.pendingPoNumber || "").trim();
      if (!po) {
        this.$nextTick(() => {
          var el = document.getElementById("topPartsPoInput");
          if (el) el.focus();
        });
        return;
      }
      var qty = Math.max(1, Math.floor(Number(this.pendingQuantity) || 1));
      var pending = this.pendingPart;
      if (!pending) {
        this.showAddModal = false;
        this.resetAddModal();
        return;
      }
      var dest = this.pendingDestination === "quote" ? "quote" : "order";

      this.addingPart = true;
      try {
        var resolved = await this.resolvePartContext(pending);
        if (!resolved._companyMachineId || !resolved._assemblyId) {
          this.$bvToast.toast(
            "We couldn't resolve the machine/assembly for " +
              pending.partCode +
              ", so it can't be added automatically. Try adding it from Search.",
            {
              title: "Couldn't add to cart",
              variant: "warning",
              solid: true,
              autoHideDelay: 9000,
            }
          );
          return;
        }

        await this.persistAdd(dest, po, qty, resolved);

        if (Vue.prototype.$cartBus) {
          Vue.prototype.$cartBus.$emit("cart-updated");
        }
        this.$bvToast.toast(
          pending.partCode +
            " added to your " +
            (dest === "quote" ? "quote" : "cart") +
            ".",
          {
            title: "Added",
            variant: "success",
            solid: true,
            autoHideDelay: 4000,
          }
        );
        this.showAddModal = false;
        this.resetAddModal();
      } catch (err) {
        var details = "";
        try {
          var resp = err && err.error && err.error.response;
          if (resp && resp.data) {
            details =
              resp.data.title ||
              resp.data.exception ||
              (resp.data.errors && JSON.stringify(resp.data.errors)) ||
              JSON.stringify(resp.data);
          } else {
            details = (err && err.message) || "";
          }
        } catch (e) {
          /* no-op */
        }
        console.warn("[TopPartsWidget → saveCart] add failed:", err);
        this.$bvToast.toast(
          "Could not add the part to your cart." +
            (details ? " Server said: " + details : ""),
          {
            title: "Cart update failed",
            variant: "danger",
            solid: true,
            autoHideDelay: 9000,
          }
        );
      } finally {
        this.addingPart = false;
      }
    },

    // Build the single-part saveCart payload and POST it. Mirrors the
    // shape Account/Index.vue used (machineId = companyMachineId,
    // configuredWear true, quantityPerMachine 0). Stores the returned
    // cart id so subsequent adds append to the same cart.
    async persistAdd(dest, poNumber, qty, resolved) {
      var isQuote = dest === "quote";
      var cartId = isQuote
        ? parseInt(localStorage.cartIdRFQ, 10) || 0
        : parseInt(localStorage.cartId, 10) || 0;
      var payload = {
        id: cartId,
        purchaseOrders: [
          {
            number: poNumber,
            assemblyId: Number(resolved._assemblyId) || 0,
            machineId: Number(resolved._companyMachineId) || 0,
            configuredWear: true,
            parts: [
              {
                id: Number(resolved.partId) || 0,
                quantity: Number(qty) || 1,
                quantityPerMachine: 0,
              },
            ],
          },
        ],
        companyId: Number(localStorage.companyId) || 0,
        specialInstructions: "",
        CreateQuote: isQuote,
      };
      var response = await apiService.saveCart(payload);
      if (response && response.id) {
        if (isQuote) {
          localStorage.cartIdRFQ = response.id;
        } else {
          localStorage.cartId = response.id;
        }
      }
      return response;
    },

    // ===== PART CONTEXT RESOLUTION =====
    // saveCart needs AssemblyId + CompanyMachineId, neither derivable
    // from partId alone. Try PartController.GetPartContext first, then
    // fall back to parsing the resolve-top-part-context redirect URL.
    // Ported from Account/Index.vue.
    async resolvePartContext(p) {
      var enriched = Object.assign({}, p, {
        _companyMachineId: null,
        _assemblyId: null,
      });
      var companyId = localStorage.companyId;

      if (typeof apiService.getPartContext === "function") {
        try {
          var ctx = await apiService.getPartContext({
            partId: p.partId,
            companyId: companyId,
          });
          if (ctx) {
            enriched._companyMachineId =
              ctx.companyMachineId || ctx.CompanyMachineId || null;
            enriched._assemblyId = ctx.assemblyId || ctx.AssemblyId || null;
            if (enriched._companyMachineId && enriched._assemblyId) {
              return enriched;
            }
          }
        } catch (e) {
          /* fall through to URL-parse path */
        }
      }

      try {
        var result = await apiService.resolveTopPartContext({
          partId: p.partId,
          partCode: p.partCode,
          companyId: companyId,
        });
        if (result && result.redirectUrl) {
          enriched._companyMachineId =
            enriched._companyMachineId ||
            this.parseUrlId(result.redirectUrl, [
              "companyMachineId",
              "machineId",
              "machineid",
            ]);
          enriched._assemblyId =
            enriched._assemblyId ||
            this.parseUrlId(result.redirectUrl, ["assemblyId", "assemblyid"]);
        }
      } catch (e) {
        console.warn(
          "[TopPartsWidget] resolveTopPartContext failed for " + p.partCode,
          e
        );
      }

      return enriched;
    },
    parseUrlId(url, paramNames) {
      if (!url || !paramNames || !paramNames.length) return null;
      try {
        var parsed = new URL(url, window.location.origin);
        for (var i = 0; i < paramNames.length; i++) {
          var v = parsed.searchParams.get(paramNames[i]);
          if (v) return v;
        }
        return null;
      } catch (e) {
        return null;
      }
    },

    // ===== STANDALONE FETCH =====
    // Pulls top-parts via the insights endpoint and copies them
    // into internalTopParts. Errors leave the internal array
    // empty so the empty state takes over — failing gracefully
    // here is better than showing a stale array or rendering
    // an error stub the user can't act on.
    fetchTopParts() {
      var self = this;
      apiService
        .getInsights({
          companyId: localStorage.companyId || "",
          allTime: this.period === "alltime",
        })
        .then(function (data) {
          var parts = (data && data.topParts) || [];
          self.internalTopParts = parts;
          self.loadTopPartThumbnails();
          self.hasAnyHistory = parts.length > 0;
          if (parts.length > 0) {
            self.hasEverHadParts = true;
          }
          self.internalLoaded = true;
        })
        .catch(function () {
          self.internalTopParts = [];
          self.hasAnyHistory = false;
          self.internalLoaded = true;
        });
    },

    // ===== PART THUMBNAILS =====
    // Fetch one assembly's image (once) and cache it in assemblyThumbs
    // keyed by assemblyId. Same getAssemblyDetails endpoint + blob prefix
    // the Search page uses. On miss/failure the row keeps the neutral
    // placeholder (the key stays null, which also blocks a refetch).
    ensureAssemblyThumb(assemblyId) {
      if (assemblyId == null || assemblyId === 0) return;
      if (Object.prototype.hasOwnProperty.call(this.assemblyThumbs, assemblyId)) return;
      this.$set(this.assemblyThumbs, assemblyId, null);
      var self = this;
      apiService
        .getAssemblyDetails({ id: assemblyId })
        .then(function (request) {
          if (request && request.imagePath) {
            self.$set(
              self.assemblyThumbs,
              assemblyId,
              self.machineImageUrl + request.imagePath
            );
          } else if (request && request.parentAssemblyId) {
            // Sub-assembly has no image of its own — fall back to its parent
            // assembly's diagram so the row shows something meaningful
            // (stored under the original id so the row lookup is unchanged).
            apiService
              .getAssemblyDetails({ id: request.parentAssemblyId })
              .then(function (parent) {
                if (parent && parent.imagePath) {
                  self.$set(
                    self.assemblyThumbs,
                    assemblyId,
                    self.machineImageUrl + parent.imagePath
                  );
                }
              })
              .catch(function () {});
          }
        })
        .catch(function () {
          /* leave as null — row shows the placeholder */
        });
    },

    // Request a thumbnail for each unique assemblyId across the currently
    // displayed top parts. Cheap + cached: a handful of assemblies fetched
    // once for the life of this (global, single) widget instance.
    loadTopPartThumbnails() {
      var self = this;
      // Iterate the CURRENTLY displayed rows (top-N slice, or all filter matches)
      // so filtering in a part ranked beyond N still loads its thumbnail.
      (this.displayParts || []).forEach(function (part) {
        if (part && part.assemblyId != null) {
          self.ensureAssemblyThumb(part.assemblyId);
        }
      });
    },

    // ===== DOCKED PART DIAGRAM (Top-5 thumbnail click) =====
    // Dock the part's assembly diagram into the floating stack and try to
    // OCR-zoom to the part's () callout (best-effort; silent no-op if the
    // assembly has no image, or OCR can't read it / finds no match).
    onTop5DockImage(part) {
      var img = part && this.assemblyThumbs[part.assemblyId];
      if (!img) return; // no diagram on file for this part's assembly
      // Claim the single app-wide dock slot — closes the Search dock so only
      // one docked diagram shows at a time.
      if (Vue.prototype.$cartBus) {
        Vue.prototype.$cartBus.$emit("dock-claimed", "top5");
      }
      this.top5DockedImg = img;
      this.top5Origin = "50% 50%";
      this.top5Zoom = 4;
      this.top5Locked = true;
      var self = this;
      var desc = (part && (part.description || part.code)) || "";
      locateCallout(img, desc)
        .then(function (loc) {
          if (!loc) return;
          if (self.top5DockedImg !== img || !self.top5Locked) return;
          self.top5Origin =
            (loc.xFrac * 100).toFixed(2) + "% " + (loc.yFrac * 100).toFixed(2) + "%";
          self.top5Zoom = 5;
        })
        .catch(function () {});
    },
    // Open the part on the Search page, where the full assembly-diagram features
    // (dock, balloon zoom, drag) live — the Top-5 widget can't host those, so we
    // navigate (user is OK with that). resolveTopPartContext returns the search
    // URL that surfaces this part; fall back to a plain /search.
    viewOnSearch(part) {
      if (!part) return;
      // Mirror the header search-bar's part-select navigation (Head.vue
      // selectOption) so a Top-5 click behaves exactly like searching the part
      // code: it autofills machine + sub-assembly and surfaces the part at the
      // top. contextDescription is "AssemblyName - MachineDescription".
      var code = part.code || part.partCode || "";
      var ctx = part.contextDescription || "";
      var dash = ctx.indexOf(" - ");
      var assemblyName = dash >= 0 ? ctx.slice(0, dash).trim() : ctx.trim();
      var machineName = dash >= 0 ? ctx.slice(dash + 3).trim() : "";
      try {
        localStorage.searchTerm = code;
        localStorage.code = code;
        delete localStorage.description;
      } catch (e) {
        /* ignore storage errors */
      }
      this.$router
        .push({
          name: "Search",
          query: {
            code: code,
            machineName: machineName,
            assembly: assemblyName,
            assemblyId: part.assemblyId || 0,
            allParts: "true",
            // Narrow the parts list to just this part on arrival (the Search
            // page reads `pf` into its filter box) — vs the header search, which
            // shows the whole assembly and only highlights the picked part.
            pf: code,
          },
        })
        .catch(function () {
          /* duplicate navigation (already on Search) is fine */
        });
    },
    closeTop5Dock() {
      this.top5Locked = false;
      this.top5DockedImg = null;
      this.top5Zoom = 4;
      this.top5Origin = "50% 50%";
    },
    top5ZoomIn() {
      this.top5Zoom = Math.min(this.top5Zoom + 1, 8);
    },
    top5ZoomOut() {
      this.top5Zoom = Math.max(this.top5Zoom - 1, 1);
    },
  },
};
</script>

<style scoped>
/* ============================================================
   INLINE PANEL CHROME

   These `.dashboard-card*` / `.dashboard-list*` / `.dashboard-empty`
   rules are ported from Account/Index.vue, where they originally
   lived ONLY inside that file's <style scoped> block. Vue scoped
   CSS tags every rule with a per-component data-attribute and does
   not pierce component boundaries — so when this widget renders as
   a child of Index.vue, none of those rules reached the markup
   below. The panel came out unstyled: a plain dark <h3> (picking up
   only the global h3 rule), no header divider, a left-aligned empty
   message, and no card chrome.

   Re-declaring them here, scoped to the widget, makes the panel
   render identically to the sibling Completed Orders / Completed
   Quotes cards on EVERY host — including standalone hosts
   (SavedCarts, OrderHistory) that never load Index.vue's styles.
   Keep in sync with the matching rules in Account/Index.vue.
   ============================================================ */

/* Card shell: white panel, red top accent, rounded + padded,
   flex column so the header sits above a growing body. The
   box-shadow here is superseded by the global embossed-card shadow
   in steele-consistency-fix.css (.dashboard-card, !important) —
   kept for parity with Index.vue. */
.dashboard-card {
  background: #fff;
  /* No full neutral side/bottom border — match the left content card
     (.create-order-form) and cart panel: a 3px brand-red top line + shadow,
     8px radius. The old 1px side border traced the rounded corner and (in
     dark/mid mode, where it was only recolored to a visible gray, not removed)
     let the black page show through the corner triangle. Red top + shadow
     define the card on their own, so the corner reads clean and matches the
     left section. */
  border-top: 3px solid #c0392b;
  border-radius: 8px;
  padding: 14px 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Per request: give the floating Top-50 card a 1px neutral border so its
   edge (notably the left side, against the page) reads as a defined card.
   Brand-red top stripe kept. The border color is set per theme so the
   rounded corner blends with the card edge instead of flashing the page
   through — the reason the side border was originally dropped in dark/mid. */
.dashboard-card.top-parts-card {
  border: 1px solid #d9dde2;
  border-top: 3px solid #c0392b;
}
html.mid-mode .dashboard-card.top-parts-card {
  border-color: #b3b5b9;
  border-top-color: #c0392b;
}
html.dark-mode .dashboard-card.top-parts-card {
  border-color: #333a42;
  border-top-color: #c0392b;
}

/* The widget root fills a flex-column host (e.g. Account's
   .right-column-flex) so the card can stretch to the column height
   and center its empty state against the full panel. Harmless in
   non-flex hosts (the cart column) where flex:1 is simply ignored
   and the card falls back to its min-height below. */
.top-parts-widget {
  display: flex;
  flex-direction: column;
  flex: 1;
}
.dashboard-card.dashboard-card-right {
  height: auto;
  margin-top: 0;
  flex: 1;
}

/* Floating mode (global right-edge stack in App.vue). The stack
   container is a fixed-width flex column shared with the cart, so
   the card must size to its own content here (not stretch to fill
   the column the way it does on the dashboard) and cap its height so
   a long list scrolls inside its own panel. */
.top-parts-floating {
  flex: 0 0 auto;
}
.top-parts-floating .top-parts-card {
  max-height: min(70vh, 540px);
  overflow-y: auto;
  /* Round the TOP corners explicitly so the grey card background follows the
     brand-red top accent line — matching the left "SEARCH FOR PART" card
     (.create-order-form, border-radius:8px) and the cart panel. Without this
     the grey poked past the red line as square corners: the sibling cart card
     renders fine because it has NO overflow, but this card sets
     overflow-y:auto (needed so a long Top-50 list scrolls inside its own
     panel), which turns it into a scroll container whose viewport paints the
     background SQUARE at the top against the red border. Re-declaring the top
     radii on the SAME element that owns the overflow ties the rounded clip to
     the scroll box so the corners stay rounded. 8px matches the Search card.
     overflow-x stays hidden (it's a vertical list — no horizontal scroll is
     wanted) so the rounded clip is honored on the left/right edges too. */
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  overflow-x: hidden;
}

/* Floor the height so the empty-state message has room to sit in
   the vertical middle (per design) even when the host doesn't
   stretch the card — e.g. the cart column, whose row is
   align-items:flex-start. A no-op once flex:1 makes the card
   taller, and harmless in the filled state (5 rows exceed it). */
.top-parts-card {
  min-height: 220px;
}

/* Header: title on the left, collapse chevron on the right, with
   a dashed divider beneath both — the line under the title. */
.dashboard-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  /* The dashed divider that used to sit here now lives under the
     controls row below (title + controls form one header block). When
     the controls row isn't rendered (controlled mode), the list's own
     top spacing is enough separation. */
}

/* ===== STANDALONE CONTROLS — period toggle + "Show N" =====
   Title + these controls read as one header block; the dashed divider
   under this row separates the header from the ranked list. */
.top-parts-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e6e6e6;
}
.tp-period {
  display: inline-flex;
  gap: 4px;
}
.tp-toggle-btn {
  border: 1px solid #d6d6d6;
  background: #fff;
  color: #444;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}
.tp-toggle-btn:hover {
  border-color: #b3b3b3;
}
.tp-toggle-btn.is-active {
  background: #2a2a2a;
  border-color: #2a2a2a;
  color: #fff;
}
.tp-show {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin: 0;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #888;
}
.tp-show-select {
  font-size: 11px;
  font-weight: 700;
  color: #222;
  border: 1px solid #d6d6d6;
  border-radius: 4px;
  padding: 2px 4px;
  background: #fff;
  cursor: pointer;
}
.tp-show-select:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.12);
}

/* Title: branded red, uppercase, tracked. (Global h3 rules in
   steele-consistency-fix.css force font-size/weight/family with
   !important — exactly as on every sibling dashboard card — so the
   color / uppercase / letter-spacing here are what brand it.) */
.dashboard-card-title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

/* Ranked parts list (filled state): one row per part. */
.dashboard-list {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
}
.dashboard-list-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
}
.dashboard-list-item:last-child {
  border-bottom: none;
}
/* ===== TOP-5 ROW THUMBNAIL =====
   Small assembly picture on the LEFT of each ranked row — same framing
   as the Search results list (white tile, thin border, soft radius,
   image contained). A neutral cube glyph stands in when the part's
   assembly has no image on file. */
.dashboard-list-thumb {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
  background: #fff;
  border: 1px solid #e3e3e3;
  border-radius: 6px;
  overflow: hidden;
}
.dashboard-list-thumb img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}
.dashboard-list-thumb-ph {
  width: 20px;
  height: 20px;
  fill: #c8ccd0;
}
/* Clickable thumbnail (docks the part's assembly diagram + OCR-zoom). */
.dashboard-list-thumb.is-clickable {
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.dashboard-list-thumb.is-clickable:hover {
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.15);
}
.dashboard-list-main {
  flex: 1 1 auto;
  min-width: 0;
}
.dashboard-list-code {
  font-size: 12px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.2px;
  margin-bottom: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dashboard-list-desc {
  font-size: 11px;
  color: #555;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}
.dashboard-list-meta {
  font-size: 10px;
  color: #888;
  letter-spacing: 0.2px;
}
.dashboard-list-meta strong {
  color: #444;
  font-weight: 700;
}
/* Price + QTY/MACH + Item # — mirrors the search-results rows so the two lists
   carry the same info/style (request: full match). */
.dashboard-list-price {
  font-size: 11px;
  font-weight: 600;
  color: #2b2b2b;
  margin-top: 1px;
}
.dashboard-list-qpm {
  font-weight: 500;
  color: #888;
}
.dashboard-list-item-box {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  padding: 2px 6px;
  margin-right: 6px;
  background: #f7f8f9;
  border: 1px solid #e3e3e3;
  border-radius: 6px;
}
.dashboard-list-item-label {
  font-size: 8px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #8a93a0;
  font-weight: 700;
}
.dashboard-list-item-val {
  font-size: 11px;
  font-weight: 700;
  color: #2b2b2b;
}
.dashboard-list-item-box.is-clickable {
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.dashboard-list-item-box.is-clickable:hover {
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.15);
}

/* Per-row add-to-cart (or edit-in-cart) icon button. */
.dashboard-add-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #fff;
  color: #7ec8e3;
  text-decoration: none;
  transition: background 0.15s ease;
}
.dashboard-add-btn:hover {
  background: #c0392b;
  color: #fff;
  text-decoration: none;
}
.dashboard-add-btn svg {
  width: 14px;
  height: 14px;
}
.dashboard-add-btn.is-edit {
  color: #2a2a2a;
}
.dashboard-add-btn.is-edit:hover {
  background: #2a2a2a;
  color: #fff;
}

/* Empty state: centered, italic, muted — and grows to fill the
   card body (flex:1) so "No purchase history yet." sits in the
   vertical middle rather than glued under the header. */
.dashboard-empty {
  padding: 16px 4px;
  font-size: 12px;
  color: #888;
  text-align: center;
  font-style: italic;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ===== PART FILTER =====
   Mirrors the Search results filter (Views/Search/Index.vue .part-filter-*) so
   the two read identically. Re-declared here (scoped) because Search's scoped
   CSS doesn't reach this widget — and on the Account / floating-stack hosts the
   Search component isn't even mounted. Keep in sync with Index.vue. */
.part-filter-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.part-filter-wrap-block {
  display: flex;
  width: 100%;
  margin: 0 0 12px;
}
.part-filter-icon {
  position: absolute;
  left: 9px;
  font-size: 12px;
  opacity: 0.55;
  pointer-events: none;
}
.part-filter-input {
  width: 100%;
  padding: 7px 26px 7px 44px;
  border: 1px solid #d4d8dd;
  border-radius: 6px;
  font-size: 13px;
  color: #16181b;
  background: #fff;
}
/* Beat any global/theme dark `input` rule so the placeholder clears the 🔍. */
.part-filter-wrap .part-filter-input {
  padding-left: 44px !important;
  text-indent: 0;
}
.part-filter-input:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.15);
}
.part-filter-clear {
  position: absolute;
  right: 6px;
  border: none;
  background: transparent;
  color: #8a93a0;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}
.part-filter-clear:hover {
  color: #c0392b;
}
/* "No matches" clear-filter link inside the empty state. */
.tpw-filter-clear-link {
  color: #c0392b;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  margin-left: 4px;
}

/* Push the collapse button to the far right of the header.
   .dashboard-card-head is already display:flex with space-
   between, so the button naturally lands on the right side
   without margin: auto. */
/* Matches CartWidget's .cart-collapse-btn exactly — a circular grey
   button with the same chevron glyph — so the two widgets' collapse
   controls are identical. */
.top-parts-collapse-btn {
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: #f0f0f0;
  color: #555;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
  /* Reset font-family so SVG inheritance doesn't pick up any
     heavy webfont from a parent button rule. */
  font-family: inherit;
  /* Pin button shape so a flex parent doesn't squish it. */
  flex: 0 0 auto;
}
.top-parts-collapse-btn:hover {
  background: #c0392b;
  color: #fff;
}
.top-parts-collapse-btn:focus-visible {
  outline: 2px solid #c0392b;
  outline-offset: 2px;
}

/* The COLLAPSED PILL's CSS lives in the UNSCOPED <style> block below.
   The pill is portaled into App.vue's .floating-pill-rail (outside this
   component's DOM), so scoped CSS can't reach it — it would render
   unstyled/transparent. It must be unscoped, exactly like CartWidget's
   .cart-pill rules. */
</style>

<style>
/* ============================================================
   COLLAPSED PILL — UNSCOPED.
   The pill is portaled into App.vue's .floating-pill-rail, so scoped CSS
   wouldn't reach it. Class names are namespaced (.top-parts-pill*) so
   these global rules are safe. Mirrors CartWidget's unscoped .cart-pill.
   Positioning is owned by the rail; the pill is a static flex child.
   ============================================================ */
.top-parts-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: #c0392b;
  border: 1px solid #ececec;
  border-left: 3px solid #c0392b;
  border-radius: 0 8px 8px 0;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.15);
  padding: 10px 8px;
  min-width: 56px;
  width: 56px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease, transform 0.18s ease;
}
.top-parts-pill:hover {
  background: #fafafa;
  transform: translateX(-2px);
}
.top-parts-pill:focus-visible {
  outline: 2px solid #c0392b;
  outline-offset: 2px;
}
.top-parts-pill-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}
.top-parts-pill-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #c0392b;
  line-height: 1;
  white-space: nowrap;
}

/* ============================================================
   ADD-TO-CART MODAL — UNSCOPED.

   b-modal renders to a body portal, so scoped CSS wouldn't reach
   it. These rules are moved here from Account/Index.vue's global
   <style> block (where the dashboard's add-to-cart modal CSS used
   to live) so the modal stays styled now that the interaction is
   owned by this widget. Class names are namespaced (.add-to-cart-*)
   and the modal carries modal-class="add-to-cart-modal", so the
   rules are safe globally. This block injects app-wide because the
   widget is globally registered (main.js) and loaded at startup.
   ============================================================ */
.add-to-cart-modal .modal-dialog {
  max-width: 480px;
}
.add-to-cart-body {
  padding: 0.25rem 0.25rem 0;
}
.add-to-cart-lede {
  font-size: 0.95rem;
  margin-bottom: 1.25rem;
  color: #333;
}
.add-to-cart-partcode {
  color: #c8102e;
  font-weight: 700;
}
.add-to-cart-section-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #555;
  margin-bottom: 0.5rem;
  margin-top: 1rem;
}
.add-to-cart-destination {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}
.add-to-cart-dest-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.85rem 0.5rem;
  border: 2px solid #d6d6d6;
  border-radius: 6px;
  background: #fff;
  color: #222;
  cursor: pointer;
  transition: all 0.12s ease-in-out;
  text-align: center;
  min-height: 70px;
}
.add-to-cart-dest-btn:hover {
  border-color: #b3b3b3;
}
.add-to-cart-dest-btn.is-active {
  background: #c8102e;
  border-color: #c8102e;
  color: #fff;
}
.add-to-cart-dest-title {
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.add-to-cart-dest-sub {
  font-size: 0.75rem;
  font-weight: 400;
  margin-top: 0.15rem;
  opacity: 0.85;
}
.add-to-cart-qty-input {
  max-width: 100px;
}
.add-to-cart-modal .po-modal-input {
  display: block;
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  color: #222;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.add-to-cart-modal .po-modal-input:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 3px rgba(192, 57, 43, 0.12);
}
.add-to-cart-po-picker {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}
.add-to-cart-po-pick-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.7rem 1rem;
  border: 2px solid #d6d6d6;
  border-radius: 6px;
  background: #fff;
  color: #222;
  cursor: pointer;
  transition: all 0.12s ease-in-out;
  text-align: left;
  width: 100%;
}
.add-to-cart-po-pick-btn:hover {
  border-color: #b3b3b3;
  background: #fafafa;
}
.add-to-cart-po-pick-btn.is-active {
  border-color: #c8102e;
  background: #fdf0f2;
}
.add-to-cart-po-pick-btn-num {
  font-weight: 700;
  font-size: 0.95rem;
  color: #222;
}
.add-to-cart-po-pick-btn-meta {
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.2rem;
}
.add-to-cart-po-pick-btn.is-active .add-to-cart-po-pick-btn-meta {
  color: #555;
}
.add-to-cart-po-pick-btn-new {
  border-style: dashed;
  align-items: center;
}
.add-to-cart-po-pick-btn-new .add-to-cart-po-pick-btn-num {
  color: #c8102e;
}
</style>